import { SeatmapOrder } from "../models/AssignedSeating";
import { Cart } from "../models/Cart";
import { HoldGroupTicketsRequest } from "../models/Event";
import { Order } from "../models/Order";
import { TokenResponse } from "../models/PaymentGateway";
import { OrderRequest } from "../requests/OrderRequests";
import axios from "./axios";
import { BuildUri } from "./BuildUri";

class SalesEndpoints {
    static async getSeasonPassHolderByTicketId(ticketId: number | string) {
        return (
            await axios.get<{
                id: number;
                firstName: string;
                lastName: string;
            }>(
                BuildUri("/sales/season-pass-holder-by-ticket/{ticketId}", {
                    ticketId,
                }),
                {
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async getOrderInfoByTicket(ticketId: number | string) {
        return (
            await axios.get<SeatmapOrder>(
                BuildUri("/sales/order-by-ticket/{ticketId}", {
                    ticketId,
                }),
                {
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async checkRegistration(eventId: number, email: string) {
        return (
            await axios.get<{ registered: boolean }>(
                BuildUri(
                    "/sales/registration-check",
                    {},
                    { eventId, email },
                ),
                { withCredentials: true },
            )
        ).data;
    }

    static async getCart(
        cartKey: string,
        discountCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.get<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}",
                    { cartKey },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                {
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async checkDiscountCode(
        cartUrl: string,
        cartKey: string,
        discountCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.get<Cart>(
                BuildUri(
                    "/sales/discount-code/{discountCode}",
                    { discountCode },
                    { cartUrl, cartKey, deliveryMethodId, customAmount },
                ),
                {
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async clearCart(cartKey: string) {
        return (
            await axios.delete<Cart>(
                BuildUri("/sales/cart/{cartKey}", { cartKey }),
                undefined,
                { withCredentials: true },
            )
        ).data;
    }

    static async setCartEmail(
        cartKey: string,
        email: string,
        emailConsent: boolean,
    ) {
        return (
            await axios.post<{ success: boolean }>(
                BuildUri("/sales/cart/{cartKey}/email", { cartKey }),
                { email, emailConsent },
                { withCredentials: true },
            )
        ).data;
    }

    static async updateGaQuantity(
        cartKey: string,
        request: any,
        discountCode: string,
        sdaCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/department",
                    { cartKey },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                request,
                {
                    headers: {
                        "X-Access-Code": sdaCode,
                    },
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async addTicketsToCart(
        cartKey: string,
        request: any,
        discountCode: string,
        sdaCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/tickets",
                    { cartKey },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                request,
                {
                    headers: {
                        "X-Access-Code": sdaCode,
                    },
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async addReservedTicketsToCart(cartKey: string, slug: string) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/season-tickets/{slug}",
                    { cartKey, slug },
                    {},
                ),
                undefined,
                {
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async addHoldGroupTicketsToCart(
        cartKey: string,
        holdGroupId: number,
        request: HoldGroupTicketsRequest,
        discountCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/hold-group/{holdGroupId}",
                    { cartKey, holdGroupId },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                request,
                {
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async addSeatmapTicketToCart(
        cartKey: string,
        request: {
            eventSeatmapId: number;
            seatId: number;
            departmentId: number;
            holdGroupSeatId: number | undefined;
            comp: boolean;
        },
        discountCode: string,
        sdaCode: string,
        deliveryMethodId: number,
        confirmAdd: boolean = false,
        customAmount?: number,
    ) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/seatmap-ticket",
                    { cartKey },
                    {
                        discountCode,
                        deliveryMethodId,
                        confirmAdd,
                        customAmount,
                    },
                ),
                request,
                {
                    headers: {
                        "X-Access-Code": sdaCode,
                    },
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async removeSeatmapTicketFromCart(
        cartKey: string,
        request: {
            eventSeatmapId: number;
            seatId: number;
        },
        discountCode: string,
        sdaCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.delete<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/seatmap-ticket",
                    { cartKey },
                    {
                        discountCode,
                        deliveryMethodId,
                        customAmount,
                    },
                ),
                request,
                {
                    headers: {
                        "X-Access-Code": sdaCode,
                    },
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async setSeatmapSectionQtyInCart(
        cartKey: string,
        request: {
            eventSeatmapId: number;
            sectionId: number;
            departmentId: number;
            holdGroupSectionId: number | undefined;
            quantity: number;
            comp: boolean;
            redemptionCodeId: number | undefined;
        },
        discountCode: string,
        sdaCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/seatmap-section",
                    { cartKey },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                request,
                {
                    headers: {
                        "X-Access-Code": sdaCode,
                    },
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async setPackageSectionQtyInCart(
        cartKey: string,
        request: {
            eventPackageId: number;
            sectionId: number;
            priceGroupId: number;
            quantity: number;
            comp: boolean;
            holdGroupEventPackageId?: number | null;
        },
        discountCode: string,
        sdaCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/event-package-section",
                    { cartKey },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                request,
                {
                    headers: {
                        "X-Access-Code": sdaCode,
                    },
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async addEventPackageToCart(
        cartKey: string,
        request: {
            eventPackageId: number;
            seatId: number;
            priceGroupId: number;
            comp: boolean;
        },
        discountCode: string,
        sdaCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/event-package",
                    { cartKey },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                request,
                {
                    headers: {
                        "X-Access-Code": sdaCode,
                    },
                    withCredentials: true,
                },
            )
        ).data;
    }

    static async removeEventPackageFromCart(
        cartKey: string,
        request: {
            statusId: number;
        },
        discountCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.delete<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/event-package",
                    { cartKey },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                request,
                { withCredentials: true },
            )
        ).data;
    }

    static async removeTicketsFromCart(
        cartKey: string,
        request: any,
        discountCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.delete<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/tickets",
                    { cartKey },
                    { discountCode, deliveryMethodId, customAmount },
                ),
                request,
                { withCredentials: true },
            )
        ).data;
    }

    static async applyRedemptionCode(
        cartKey: string,
        redemptionCode: string,
        discountCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.post<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/redemption/{redemptionCode}",
                    { cartKey, redemptionCode },
                    { discountCode, deliveryMethodId, customAmount },
                ),
            )
        ).data;
    }

    static async removeRedemptionCode(
        cartKey: string,
        redemptionCodeId: number,
        discountCode: string,
        deliveryMethodId: number,
        customAmount?: number,
    ) {
        return (
            await axios.delete<Cart>(
                BuildUri(
                    "/sales/cart/{cartKey}/redemption/{redemptionCodeId}",
                    { cartKey, redemptionCodeId },
                    { discountCode, deliveryMethodId, customAmount },
                ),
            )
        ).data;
    }

    static async toggleSeatStatus(
        eventId: number,
        seatId: number,
        departmentId: number,
        enable: boolean,
    ) {
        return (
            await axios.post<void>(
                BuildUri(
                    "/sales/seating/{eventId}/reserve",
                    { eventId },
                    { departmentId, seatId, enable },
                ),
                undefined,
            )
        ).data;
    }

    // Payment Processing
    static async getCardinalToken(cartKey: string, discountCode: string) {
        return (
            await axios.get<TokenResponse>(
                BuildUri(
                    "/sales/cardinal-token",
                    {},
                    { cartKey, discountCode },
                ),
            )
        ).data;
    }

    static async completeOrder(request: OrderRequest) {
        return (await axios.post<Order>(BuildUri("/sales/order"), request))
            .data;
    }

    static async cancelOrder(request: {
        retrievalKey: string;
        cartKey: string;
    }) {
        return (await axios.post<Order>(BuildUri("/stripe/cancel"), request))
            .data;
    }

    static async getOrderByRetrievalKey(retrievalKey: string) {
        return (
            await axios.get<Order>(
                BuildUri("/sales/order/{retrievalKey}", { retrievalKey }),
            )
        ).data;
    }

    static async cancelSquareTerminalCheckout(retrievalKey: string) {
        return (
            await axios.delete<void>(
                BuildUri("/sales/square-checkout/{retrievalKey}", {
                    retrievalKey,
                }),
            )
        ).data;
    }
}

export default SalesEndpoints;
